import {ChangeDetectorRef, Component, Input} from '@angular/core';
import {BlockBaseComponent} from "../../scene-blocks/block-base/block-base.component";
import {isHostDevOrLocal} from "../../../../common";
import {IEffectName, IEffectTypeAny} from "../../../../interfaces";
import {TranslocoService} from "@ngneat/transloco";
import {FieldConfig} from "../../../../interfaces/field.interface";
import {LocalStoreService, StoreSelectorsService} from "../../../../services";
import {Lib} from "../../../../../libs/Lib";
import * as _ from "lodash";
import {trigger, state, style, transition, animate} from '@angular/animations';

@Component({
    selector: 'app-block-effect-props',
    templateUrl: './block-effect-props.component.html',
    styleUrls: ['./block-effect-props.component.scss'],
    animations: [
        trigger('slideDown', [
            transition(':enter', [
                style({ height: '0', opacity: 0, overflow: 'hidden' }),
                animate('300ms ease-out', style({ height: '*', opacity: 1 }))
            ]),
            transition(':leave', [
                animate('300ms ease-in', style({ height: '0', opacity: 0, overflow: 'hidden' }))
            ])
        ])
    ],
    standalone: false
})

export class BlockEffectPropsComponent extends BlockBaseComponent {
    effectStorePathRoot;
    effect: IEffectName;
    isHostDevOrLocal;
    showAdvanced = false;
    shadowPresets = [
        { name: 'subtle shadow', preview: '2px 2px 4px rgba(0,0,0,0.2)', values: { color: '#000000', alpha: 0.2, blur: 4, angle: 45, distance: 3, resolution: 1, blurX: 2, blurY: 2, strength: 1 } },
        { name: 'classic drop', preview: '4px 4px 8px rgba(0,0,0,0.4)', values: { color: '#000000', alpha: 0.4, blur: 8, angle: 45, distance: 6, resolution: 1, blurX: 4, blurY: 4, strength: 1 } },
        { name: 'hard edge', preview: '6px 6px 2px rgba(0,0,0,0.8)', values: { color: '#000000', alpha: 0.8, blur: 2, angle: 45, distance: 8, resolution: 1, blurX: 1, blurY: 1, strength: 2 } },
        { name: 'soft glow', preview: '0px 0px 20px rgba(0,0,0,0.3)', values: { color: '#000000', alpha: 0.3, blur: 20, angle: 0, distance: 0, resolution: 1, blurX: 20, blurY: 20, strength: 0.5 } },
        { name: 'long cast', preview: '15px 15px 10px rgba(0,0,0,0.5)', values: { color: '#000000', alpha: 0.5, blur: 10, angle: 45, distance: 20, resolution: 1, blurX: 8, blurY: 8, strength: 1 } },
        { name: 'inner depth', preview: '1px 1px 3px rgba(0,0,0,0.9)', values: { color: '#000000', alpha: 0.9, blur: 3, angle: 135, distance: 2, resolution: 1, blurX: 2, blurY: 2, strength: 2 } },
        { name: 'neon glow', preview: '0px 0px 15px rgba(0,150,255,0.8)', values: { color: '#0096ff', alpha: 0.8, blur: 15, angle: 0, distance: 0, resolution: 1, blurX: 15, blurY: 15, strength: 1.5 } },
        { name: 'lifted', preview: '0px 8px 16px rgba(0,0,0,0.4)', values: { color: '#000000', alpha: 0.4, blur: 16, angle: 90, distance: 10, resolution: 1, blurX: 10, blurY: 10, strength: 1 } },
        { name: 'bold pop', preview: '8px 8px 0px rgba(0,0,0,1)', values: { color: '#000000', alpha: 1, blur: 0, angle: 45, distance: 10, resolution: 1, blurX: 0, blurY: 0, strength: 3 } },
        { name: 'colored soft', preview: '0px 4px 12px rgba(255,100,150,0.6)', values: { color: '#ff6496', alpha: 0.6, blur: 12, angle: 90, distance: 5, resolution: 1, blurX: 12, blurY: 12, strength: 1 } },
        { name: 'paper lift', preview: '0px 3px 6px rgba(0,0,0,0.15)', values: { color: '#000000', alpha: 0.15, blur: 6, angle: 90, distance: 4, resolution: 1, blurX: 6, blurY: 6, strength: 0.8 } },
        { name: 'deep cast', preview: '12px 12px 25px rgba(0,0,0,0.6)', values: { color: '#000000', alpha: 0.6, blur: 25, angle: 45, distance: 18, resolution: 1, blurX: 15, blurY: 15, strength: 1.2 } },
        { name: 'sharp drop', preview: '5px 5px 0px rgba(0,0,0,0.7)', values: { color: '#000000', alpha: 0.7, blur: 0, angle: 45, distance: 7, resolution: 1, blurX: 0, blurY: 0, strength: 2.5 } },
        { name: 'bottom only', preview: '0px 6px 10px rgba(0,0,0,0.35)', values: { color: '#000000', alpha: 0.35, blur: 10, angle: 90, distance: 8, resolution: 1, blurX: 8, blurY: 8, strength: 1 } },
        { name: 'right side', preview: '8px 0px 12px rgba(0,0,0,0.3)', values: { color: '#000000', alpha: 0.3, blur: 12, angle: 0, distance: 10, resolution: 1, blurX: 10, blurY: 10, strength: 1 } },
        { name: 'left side', preview: '-8px 0px 12px rgba(0,0,0,0.3)', values: { color: '#000000', alpha: 0.3, blur: 12, angle: 180, distance: 10, resolution: 1, blurX: 10, blurY: 10, strength: 1 } },
        { name: 'purple haze', preview: '0px 0px 18px rgba(150,0,255,0.7)', values: { color: '#9600ff', alpha: 0.7, blur: 18, angle: 0, distance: 0, resolution: 1, blurX: 18, blurY: 18, strength: 1.3 } },
        { name: 'red glow', preview: '0px 0px 15px rgba(244,67,54,0.8)', values: { color: '#f44336', alpha: 0.8, blur: 15, angle: 0, distance: 0, resolution: 1, blurX: 15, blurY: 15, strength: 1.5 } },
        { name: 'green aura', preview: '0px 0px 20px rgba(76,175,80,0.6)', values: { color: '#4caf50', alpha: 0.6, blur: 20, angle: 0, distance: 0, resolution: 1, blurX: 20, blurY: 20, strength: 1.2 } },
        { name: 'inset dark', preview: '2px 2px 5px rgba(0,0,0,0.8)', values: { color: '#000000', alpha: 0.8, blur: 5, angle: 45, distance: 3, resolution: 1, blurX: 3, blurY: 3, strength: 2 } },
        { name: 'floating', preview: '0px 12px 24px rgba(0,0,0,0.3)', values: { color: '#000000', alpha: 0.3, blur: 24, angle: 90, distance: 15, resolution: 1, blurX: 15, blurY: 15, strength: 1 } },
        { name: 'orange glow', preview: '0px 0px 18px rgba(255,152,0,0.8)', values: { color: '#ff9800', alpha: 0.8, blur: 18, angle: 0, distance: 0, resolution: 1, blurX: 18, blurY: 18, strength: 1.4 } },
        { name: 'cyan glow', preview: '0px 0px 16px rgba(0,188,212,0.7)', values: { color: '#00bcd4', alpha: 0.7, blur: 16, angle: 0, distance: 0, resolution: 1, blurX: 16, blurY: 16, strength: 1.3 } },
        { name: 'top shadow', preview: '0px -6px 10px rgba(0,0,0,0.35)', values: { color: '#000000', alpha: 0.35, blur: 10, angle: 270, distance: 8, resolution: 1, blurX: 8, blurY: 8, strength: 1 } }
    ];

    blurPresets = [
        { name: 'light blur', preview: 'blur(2px)', values: { blurX: 2, blurY: 2, resolution: 1 } },
        { name: 'soft focus', preview: 'blur(5px)', values: { blurX: 5, blurY: 5, resolution: 1 } },
        { name: 'medium blur', preview: 'blur(10px)', values: { blurX: 10, blurY: 10, resolution: 1 } },
        { name: 'heavy blur', preview: 'blur(20px)', values: { blurX: 20, blurY: 20, resolution: 1 } },
        { name: 'horizontal', preview: 'blur(15px) vertical', values: { blurX: 15, blurY: 0, resolution: 1 } },
        { name: 'vertical', preview: 'blur(15px) horizontal', values: { blurX: 0, blurY: 15, resolution: 1 } },
        { name: 'intense', preview: 'blur(30px)', values: { blurX: 30, blurY: 30, resolution: 2 } },
        { name: 'dreamy', preview: 'blur(25px)', values: { blurX: 25, blurY: 25, resolution: 1 } },
        { name: 'subtle', preview: 'blur(3px)', values: { blurX: 3, blurY: 3, resolution: 1 } },
        { name: 'gentle', preview: 'blur(7px)', values: { blurX: 7, blurY: 7, resolution: 1 } },
        { name: 'strong', preview: 'blur(15px)', values: { blurX: 15, blurY: 15, resolution: 1 } },
        { name: 'extra heavy', preview: 'blur(35px)', values: { blurX: 35, blurY: 35, resolution: 2 } },
        { name: 'horizontal light', preview: 'blur(5px) vertical', values: { blurX: 5, blurY: 0, resolution: 1 } },
        { name: 'horizontal strong', preview: 'blur(25px) vertical', values: { blurX: 25, blurY: 0, resolution: 1 } },
        { name: 'vertical light', preview: 'blur(5px) horizontal', values: { blurX: 0, blurY: 5, resolution: 1 } },
        { name: 'vertical strong', preview: 'blur(25px) horizontal', values: { blurX: 0, blurY: 25, resolution: 1 } },
        { name: 'motion blur h', preview: 'blur(20px) vertical', values: { blurX: 20, blurY: 0, resolution: 1 } },
        { name: 'motion blur v', preview: 'blur(20px) horizontal', values: { blurX: 0, blurY: 20, resolution: 1 } },
        { name: 'ultra soft', preview: 'blur(40px)', values: { blurX: 40, blurY: 40, resolution: 2 } },
        { name: 'maximum', preview: 'blur(50px)', values: { blurX: 50, blurY: 50, resolution: 2 } },
        { name: 'minimal', preview: 'blur(1px)', values: { blurX: 1, blurY: 1, resolution: 1 } },
        { name: 'moderate', preview: 'blur(12px)', values: { blurX: 12, blurY: 12, resolution: 1 } },
        { name: 'diagonal', preview: 'blur(18px)', values: { blurX: 18, blurY: 18, resolution: 1 } },
        { name: 'extreme', preview: 'blur(60px)', values: { blurX: 60, blurY: 60, resolution: 2 } }
    ];

    glowPresets = [
        { name: 'soft glow', preview: '0 0 10px rgba(255,255,255,0.5)', values: { color: '#ffffff', alpha: 0.5, blur: 4, resolution: 1 } },
        { name: 'neon blue', preview: '0 0 20px rgba(0,150,255,0.8)', values: { color: '#0096ff', alpha: 0.8, blur: 4, resolution: 1 } },
        { name: 'neon pink', preview: '0 0 20px rgba(255,0,150,0.8)', values: { color: '#ff0096', alpha: 0.8, blur: 4, resolution: 1 } },
        { name: 'fire glow', preview: '0 0 25px rgba(255,100,0,0.9)', values: { color: '#ff6400', alpha: 0.9, blur: 4, resolution: 1 } },
        { name: 'electric', preview: '0 0 30px rgba(100,255,255,0.9)', values: { color: '#64ffff', alpha: 0.9, blur: 4, resolution: 2 } },
        { name: 'subtle white', preview: '0 0 8px rgba(255,255,255,0.3)', values: { color: '#ffffff', alpha: 0.3, blur: 4, resolution: 1 } },
        { name: 'toxic green', preview: '0 0 20px rgba(0,255,0,0.8)', values: { color: '#00ff00', alpha: 0.8, blur: 4, resolution: 1 } },
        { name: 'purple haze', preview: '0 0 25px rgba(150,0,255,0.7)', values: { color: '#9600ff', alpha: 0.7, blur: 4, resolution: 1 } },
        { name: 'red hot', preview: '0 0 22px rgba(244,67,54,0.9)', values: { color: '#f44336', alpha: 0.9, blur: 5, resolution: 1 } },
        { name: 'amber warm', preview: '0 0 18px rgba(255,193,7,0.7)', values: { color: '#ffc107', alpha: 0.7, blur: 4, resolution: 1 } },
        { name: 'teal cool', preview: '0 0 16px rgba(0,150,136,0.8)', values: { color: '#009688', alpha: 0.8, blur: 4, resolution: 1 } },
        { name: 'indigo deep', preview: '0 0 24px rgba(63,81,181,0.8)', values: { color: '#3f51b5', alpha: 0.8, blur: 5, resolution: 1 } },
        { name: 'lime bright', preview: '0 0 20px rgba(205,220,57,0.8)', values: { color: '#cddc39', alpha: 0.8, blur: 4, resolution: 1 } },
        { name: 'orange vibrant', preview: '0 0 22px rgba(255,152,0,0.85)', values: { color: '#ff9800', alpha: 0.85, blur: 4, resolution: 1 } },
        { name: 'cyan bright', preview: '0 0 18px rgba(0,188,212,0.8)', values: { color: '#00bcd4', alpha: 0.8, blur: 4, resolution: 1 } },
        { name: 'purple deep', preview: '0 0 26px rgba(156,39,176,0.75)', values: { color: '#9c27b0', alpha: 0.75, blur: 5, resolution: 1 } },
        { name: 'green forest', preview: '0 0 20px rgba(76,175,80,0.7)', values: { color: '#4caf50', alpha: 0.7, blur: 4, resolution: 1 } },
        { name: 'blue sky', preview: '0 0 17px rgba(3,169,244,0.8)', values: { color: '#03a9f4', alpha: 0.8, blur: 4, resolution: 1 } },
        { name: 'yellow sun', preview: '0 0 21px rgba(255,235,59,0.75)', values: { color: '#ffeb3b', alpha: 0.75, blur: 4, resolution: 1 } },
        { name: 'pink hot', preview: '0 0 19px rgba(233,30,99,0.85)', values: { color: '#e91e63', alpha: 0.85, blur: 4, resolution: 1 } },
        { name: 'white intense', preview: '0 0 28px rgba(255,255,255,0.9)', values: { color: '#ffffff', alpha: 0.9, blur: 6, resolution: 2 } },
        { name: 'deep purple', preview: '0 0 25px rgba(103,58,183,0.8)', values: { color: '#673ab7', alpha: 0.8, blur: 5, resolution: 1 } },
        { name: 'light green', preview: '0 0 18px rgba(139,195,74,0.75)', values: { color: '#8bc34a', alpha: 0.75, blur: 4, resolution: 1 } },
        { name: 'deep orange', preview: '0 0 23px rgba(255,87,34,0.85)', values: { color: '#ff5722', alpha: 0.85, blur: 4, resolution: 1 } }
    ];

    borderPresets = [
        { name: 'thin black', preview: '1px solid #000', values: { borderColor: '#000000', borderThickness: 1 } },
        { name: 'medium black', preview: '3px solid #000', values: { borderColor: '#000000', borderThickness: 3 } },
        { name: 'thick black', preview: '6px solid #000', values: { borderColor: '#000000', borderThickness: 6 } },
        { name: 'thin white', preview: '1px solid #fff', values: { borderColor: '#ffffff', borderThickness: 1 } },
        { name: 'medium white', preview: '3px solid #fff', values: { borderColor: '#ffffff', borderThickness: 3 } },
        { name: 'thick white', preview: '6px solid #fff', values: { borderColor: '#ffffff', borderThickness: 6 } },
        { name: 'blue accent', preview: '4px solid #2196F3', values: { borderColor: '#2196F3', borderThickness: 4 } },
        { name: 'bold red', preview: '5px solid #f44336', values: { borderColor: '#f44336', borderThickness: 5 } },
        { name: 'pink', preview: '3px solid #e91e63', values: { borderColor: '#e91e63', borderThickness: 3 } },
        { name: 'purple', preview: '3px solid #9c27b0', values: { borderColor: '#9c27b0', borderThickness: 3 } },
        { name: 'deep purple', preview: '3px solid #673ab7', values: { borderColor: '#673ab7', borderThickness: 3 } },
        { name: 'indigo', preview: '3px solid #3f51b5', values: { borderColor: '#3f51b5', borderThickness: 3 } },
        { name: 'cyan', preview: '3px solid #00bcd4', values: { borderColor: '#00bcd4', borderThickness: 3 } },
        { name: 'teal', preview: '3px solid #009688', values: { borderColor: '#009688', borderThickness: 3 } },
        { name: 'green', preview: '3px solid #4caf50', values: { borderColor: '#4caf50', borderThickness: 3 } },
        { name: 'lime', preview: '3px solid #cddc39', values: { borderColor: '#cddc39', borderThickness: 3 } },
        { name: 'yellow', preview: '3px solid #ffeb3b', values: { borderColor: '#ffeb3b', borderThickness: 3 } },
        { name: 'amber', preview: '3px solid #ffc107', values: { borderColor: '#ffc107', borderThickness: 3 } },
        { name: 'orange', preview: '3px solid #ff9800', values: { borderColor: '#ff9800', borderThickness: 3 } },
        { name: 'deep orange', preview: '3px solid #ff5722', values: { borderColor: '#ff5722', borderThickness: 3 } },
        { name: 'brown', preview: '3px solid #795548', values: { borderColor: '#795548', borderThickness: 3 } },
        { name: 'grey', preview: '3px solid #9e9e9e', values: { borderColor: '#9e9e9e', borderThickness: 3 } },
        { name: 'extra thick black', preview: '8px solid #000', values: { borderColor: '#000000', borderThickness: 8 } },
        { name: 'extra thick white', preview: '8px solid #fff', values: { borderColor: '#ffffff', borderThickness: 8 } }
    ];

    backgroundPresets = [
        { name: 'transparent', preview: 'transparent', values: { color: '#000000', alpha: 0 } },
        { name: 'black subtle', preview: 'rgba(0,0,0,0.3)', values: { color: '#000000', alpha: 0.3 } },
        { name: 'black medium', preview: 'rgba(0,0,0,0.6)', values: { color: '#000000', alpha: 0.6 } },
        { name: 'black solid', preview: '#000000', values: { color: '#000000', alpha: 1 } },
        { name: 'white subtle', preview: 'rgba(255,255,255,0.3)', values: { color: '#ffffff', alpha: 0.3 } },
        { name: 'white medium', preview: 'rgba(255,255,255,0.6)', values: { color: '#ffffff', alpha: 0.6 } },
        { name: 'white solid', preview: '#ffffff', values: { color: '#ffffff', alpha: 1 } },
        { name: 'blue overlay', preview: 'rgba(33,150,243,0.5)', values: { color: '#2196F3', alpha: 0.5 } },
        { name: 'red', preview: '#f44336', values: { color: '#f44336', alpha: 1 } },
        { name: 'pink', preview: '#e91e63', values: { color: '#e91e63', alpha: 1 } },
        { name: 'purple', preview: '#9c27b0', values: { color: '#9c27b0', alpha: 1 } },
        { name: 'deep purple', preview: '#673ab7', values: { color: '#673ab7', alpha: 1 } },
        { name: 'indigo', preview: '#3f51b5', values: { color: '#3f51b5', alpha: 1 } },
        { name: 'blue', preview: '#2196F3', values: { color: '#2196F3', alpha: 1 } },
        { name: 'cyan', preview: '#00bcd4', values: { color: '#00bcd4', alpha: 1 } },
        { name: 'teal', preview: '#009688', values: { color: '#009688', alpha: 1 } },
        { name: 'green', preview: '#4caf50', values: { color: '#4caf50', alpha: 1 } },
        { name: 'lime', preview: '#cddc39', values: { color: '#cddc39', alpha: 1 } },
        { name: 'yellow', preview: '#ffeb3b', values: { color: '#ffeb3b', alpha: 1 } },
        { name: 'amber', preview: '#ffc107', values: { color: '#ffc107', alpha: 1 } },
        { name: 'orange', preview: '#ff9800', values: { color: '#ff9800', alpha: 1 } },
        { name: 'deep orange', preview: '#ff5722', values: { color: '#ff5722', alpha: 1 } },
        { name: 'brown', preview: '#795548', values: { color: '#795548', alpha: 1 } },
        { name: 'grey', preview: '#9e9e9e', values: { color: '#9e9e9e', alpha: 1 } }
    ];

    gradientPresets = [
        { name: 'ocean blue', preview: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', values: { color0: '#667eea', alpha0: 1, color1: '#764ba2', alpha1: 1, color2: '#667eea', alpha2: 0, color3: '#000000', alpha3: 0 } },
        { name: 'sunset', preview: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', values: { color0: '#f093fb', alpha0: 1, color1: '#f5576c', alpha1: 1, color2: '#000000', alpha2: 0, color3: '#000000', alpha3: 0 } },
        { name: 'forest', preview: 'linear-gradient(135deg, #0ba360 0%, #3cba92 100%)', values: { color0: '#0ba360', alpha0: 1, color1: '#3cba92', alpha1: 1, color2: '#000000', alpha2: 0, color3: '#000000', alpha3: 0 } },
        { name: 'fire', preview: 'linear-gradient(135deg, #f83600 0%, #f9d423 100%)', values: { color0: '#f83600', alpha0: 1, color1: '#f9d423', alpha1: 1, color2: '#000000', alpha2: 0, color3: '#000000', alpha3: 0 } },
        { name: 'midnight', preview: 'linear-gradient(135deg, #232526 0%, #414345 100%)', values: { color0: '#232526', alpha0: 1, color1: '#414345', alpha1: 1, color2: '#000000', alpha2: 0, color3: '#000000', alpha3: 0 } },
        { name: 'candy', preview: 'linear-gradient(135deg, #d53369 0%, #daae51 100%)', values: { color0: '#d53369', alpha0: 1, color1: '#daae51', alpha1: 1, color2: '#000000', alpha2: 0, color3: '#000000', alpha3: 0 } },
        { name: 'sky', preview: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', values: { color0: '#4facfe', alpha0: 1, color1: '#00f2fe', alpha1: 1, color2: '#000000', alpha2: 0, color3: '#000000', alpha3: 0 } },
        { name: 'purple dream', preview: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', values: { color0: '#a8edea', alpha0: 1, color1: '#fed6e3', alpha1: 1, color2: '#000000', alpha2: 0, color3: '#000000', alpha3: 0 } },
        { name: 'cherry', preview: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)', values: { color0: '#eb3349', alpha0: 1, color1: '#f45c43', alpha1: 1, color2: '#000000', alpha2: 0, color3: '#000000', alpha3: 0 } },
        { name: 'peachy', preview: 'linear-gradient(135deg, #ed4264 0%, #ffedbc 100%)', values: { color0: '#ed4264', alpha0: 1, color1: '#ffedbc', alpha1: 1, color2: '#000000', alpha2: 0, color3: '#000000', alpha3: 0 } },
        { name: 'mojito', preview: 'linear-gradient(135deg, #1d976c 0%, #93f9b9 100%)', values: { color0: '#1d976c', alpha0: 1, color1: '#93f9b9', alpha1: 1, color2: '#000000', alpha2: 0, color3: '#000000', alpha3: 0 } },
        { name: 'juicy orange', preview: 'linear-gradient(135deg, #ff8008 0%, #ffc837 100%)', values: { color0: '#ff8008', alpha0: 1, color1: '#ffc837', alpha1: 1, color2: '#000000', alpha2: 0, color3: '#000000', alpha3: 0 } },
        { name: 'cosmic', preview: 'linear-gradient(135deg, #ff00cc 0%, #333399 100%)', values: { color0: '#ff00cc', alpha0: 1, color1: '#333399', alpha1: 1, color2: '#000000', alpha2: 0, color3: '#000000', alpha3: 0 } },
        { name: 'velvet sun', preview: 'linear-gradient(135deg, #e1eec3 0%, #f05053 100%)', values: { color0: '#e1eec3', alpha0: 1, color1: '#f05053', alpha1: 1, color2: '#000000', alpha2: 0, color3: '#000000', alpha3: 0 } },
        { name: 'bloody mary', preview: 'linear-gradient(135deg, #ff512f 0%, #dd2476 100%)', values: { color0: '#ff512f', alpha0: 1, color1: '#dd2476', alpha1: 1, color2: '#000000', alpha2: 0, color3: '#000000', alpha3: 0 } },
        { name: 'virgin', preview: 'linear-gradient(135deg, #c9ffbf 0%, #ffafbd 100%)', values: { color0: '#c9ffbf', alpha0: 1, color1: '#ffafbd', alpha1: 1, color2: '#000000', alpha2: 0, color3: '#000000', alpha3: 0 } },
        { name: 'fresh turboscent', preview: 'linear-gradient(135deg, #f1f2b5 0%, #135058 100%)', values: { color0: '#f1f2b5', alpha0: 1, color1: '#135058', alpha1: 1, color2: '#000000', alpha2: 0, color3: '#000000', alpha3: 0 } },
        { name: 'king yna', preview: 'linear-gradient(135deg, #1a2a6c 0%, #b21f1f 50%, #fdbb2d 100%)', values: { color0: '#1a2a6c', alpha0: 1, color1: '#b21f1f', alpha1: 1, color2: '#fdbb2d', alpha2: 1, color3: '#000000', alpha3: 0 } },
        { name: 'stellar', preview: 'linear-gradient(135deg, #7474bf 0%, #348ac7 100%)', values: { color0: '#7474bf', alpha0: 1, color1: '#348ac7', alpha1: 1, color2: '#000000', alpha2: 0, color3: '#000000', alpha3: 0 } },
        { name: 'argon', preview: 'linear-gradient(135deg, #03001e 0%, #7303c0 50%, #ec38bc 100%)', values: { color0: '#03001e', alpha0: 1, color1: '#7303c0', alpha1: 1, color2: '#ec38bc', alpha2: 1, color3: '#000000', alpha3: 0 } },
        { name: 'flickr', preview: 'linear-gradient(135deg, #ff0084 0%, #33001b 100%)', values: { color0: '#ff0084', alpha0: 1, color1: '#33001b', alpha1: 1, color2: '#000000', alpha2: 0, color3: '#000000', alpha3: 0 } },
        { name: 'kyoto', preview: 'linear-gradient(135deg, #c21500 0%, #ffc500 100%)', values: { color0: '#c21500', alpha0: 1, color1: '#ffc500', alpha1: 1, color2: '#000000', alpha2: 0, color3: '#000000', alpha3: 0 } },
        { name: 'sweet morning', preview: 'linear-gradient(135deg, #ff5f6d 0%, #ffc371 100%)', values: { color0: '#ff5f6d', alpha0: 1, color1: '#ffc371', alpha1: 1, color2: '#000000', alpha2: 0, color3: '#000000', alpha3: 0 } },
        { name: 'selenium', preview: 'linear-gradient(135deg, #3c3b3f 0%, #605c3c 100%)', values: { color0: '#3c3b3f', alpha0: 1, color1: '#605c3c', alpha1: 1, color2: '#000000', alpha2: 0, color3: '#000000', alpha3: 0 } }
    ];

    appearancePresets = [
        { name: 'normal', preview: 'opacity: 1', values: { alpha: 1 } },
        { name: 'subtle', preview: 'opacity: 0.95', values: { alpha: 0.95 } },
        { name: 'soft', preview: 'opacity: 0.9', values: { alpha: 0.9 } },
        { name: 'light', preview: 'opacity: 0.85', values: { alpha: 0.85 } },
        { name: 'gentle', preview: 'opacity: 0.8', values: { alpha: 0.8 } },
        { name: 'medium-high', preview: 'opacity: 0.75', values: { alpha: 0.75 } },
        { name: 'medium', preview: 'opacity: 0.7', values: { alpha: 0.7 } },
        { name: 'medium-low', preview: 'opacity: 0.65', values: { alpha: 0.65 } },
        { name: 'moderate', preview: 'opacity: 0.6', values: { alpha: 0.6 } },
        { name: 'faded', preview: 'opacity: 0.55', values: { alpha: 0.55 } },
        { name: 'semi-transparent', preview: 'opacity: 0.5', values: { alpha: 0.5 } },
        { name: 'translucent', preview: 'opacity: 0.45', values: { alpha: 0.45 } },
        { name: 'ghostly', preview: 'opacity: 0.4', values: { alpha: 0.4 } },
        { name: 'ethereal', preview: 'opacity: 0.35', values: { alpha: 0.35 } },
        { name: 'wispy', preview: 'opacity: 0.3', values: { alpha: 0.3 } },
        { name: 'faint', preview: 'opacity: 0.25', values: { alpha: 0.25 } },
        { name: 'barely visible', preview: 'opacity: 0.2', values: { alpha: 0.2 } },
        { name: 'very faint', preview: 'opacity: 0.15', values: { alpha: 0.15 } },
        { name: 'whisper', preview: 'opacity: 0.1', values: { alpha: 0.1 } },
        { name: 'trace', preview: 'opacity: 0.05', values: { alpha: 0.05 } },
        { name: 'almost invisible', preview: 'opacity: 0.02', values: { alpha: 0.02 } },
        { name: 'invisible', preview: 'opacity: 0', values: { alpha: 0 } },
        { name: 'bright', preview: 'opacity: 0.88', values: { alpha: 0.88 } },
        { name: 'clear', preview: 'opacity: 0.92', values: { alpha: 0.92 } }
    ];

    @Input()
    set effectName(i_effectName) {
        this.effect = i_effectName;
        this.effectStorePathRoot = `Data[0].${this.effect}[0]`;
    }

    constructor(
        private translocoService: TranslocoService,
        public override storeSelector: StoreSelectorsService,
        private cd: ChangeDetectorRef,
        protected override localStoreService: LocalStoreService
    ) {
        super(storeSelector, undefined, localStoreService);
        this.isHostDevOrLocal = isHostDevOrLocal();
    }

    override preInit() {
    }

    override componentInit() {
        super.componentInit();
    }

    override componentStoreUpdates() {
        this.initApplyStoreToDynamic(true);
        if (this.sceneItemSwitched) {
            // this.cd.detectChanges();
        }
        super.componentStoreUpdates();
    }

    override initApplyStoreToDynamic(applyStoreToDynamic: boolean = false) {
        this.uiConfigInit.forEach((k) => {
            if (!k || _.isUndefined(k.storePath)) return;
            k.storePath = `Data[0].${this.effect}[0].$.${k.name}`;
            this.setUiConfigHide(k.name, k.hideMe);
        });
        super.initApplyStoreToDynamic(applyStoreToDynamic);
    }

    override initRegisterDynamic() {
        const resolution: FieldConfig = {
            type: "input",
            value: '',
            label: this.translocoService.translate('general.resolution', {}, this.translocoService.getActiveLang()),
            inputType: "number_rich",
            min: 0,
            step: 1,
            name: "resolution",
            storePath: ''
        };
        const blur: FieldConfig = {
            type: "input",
            value: '',
            label: this.translocoService.translate('general.blur', {}, this.translocoService.getActiveLang()),
            inputType: "number_rich",
            min: 0,
            step: 1,
            name: "blur",
            storePath: ''
        };
        const alpha: FieldConfig = {
            type: "input",
            value: '',
            label: this.translocoService.translate('general.alpha', {}, this.translocoService.getActiveLang()),
            inputType: "number_rich",
            min: 0,
            max: 1,
            step: 0.1,
            name: "alpha",
            storePath: ''
        };
        const borderThickness: FieldConfig = {
            type: "input",
            value: '',
            label: this.translocoService.translate('general.thickness', {}, this.translocoService.getActiveLang()),
            inputType: "number_rich",
            min: 0,
            max: 999,
            step: 1,
            name: "borderThickness",
            storePath: ''
        };
        const color: FieldConfig = {
            type: "input",
            value: '',
            label: this.translocoService.translate('general.color', {}, this.translocoService.getActiveLang()),
            inputType: "color",
            name: "color",
            storePath: ''
        };
        const borderColor: FieldConfig = {
            type: "input",
            value: '',
            label: this.translocoService.translate('general.borderColor', {}, this.translocoService.getActiveLang()),
            inputType: "color",
            name: "borderColor",
            storePath: ''
        };
        
        switch (this.effect) {
            case 'Appearance': {
                this.uiConfigInit = [
                    alpha,
                    // Blend mode temporarily disabled - not supported yet
                    // {
                    //     type: "select",
                    //     label: this.translocoService.translate('general.blendMode', {}, this.translocoService.getActiveLang()),
                    //     name: "blendMode",
                    //     value: "",
                    //     ignoreMissingValue: true,
                    //     options: [
                    //         {
                    //             optionsLabel: 'Normal',
                    //             optionValue: 'normal',
                    //             optionType: 'text'
                    //         },
                    //         {
                    //             optionsLabel: 'Alpha',
                    //             optionValue: 'alpha',
                    //             optionType: 'text'
                    //         },
                    //         {
                    //             optionsLabel: 'Add',
                    //             optionValue: 'add',
                    //             optionType: 'text'
                    //         },
                    //         {
                    //             optionsLabel: 'Darken',
                    //             optionValue: 'darken',
                    //             optionType: 'text'
                    //         },
                    //         {
                    //             optionsLabel: 'Difference',
                    //             optionValue: 'difference',
                    //             optionType: 'text'
                    //         },
                    //         {
                    //             optionsLabel: 'Erase',
                    //             optionValue: 'erase',
                    //             optionType: 'text'
                    //         },
                    //         {
                    //             optionsLabel: 'Hardlight',
                    //             optionValue: 'hardlight',
                    //             optionType: 'text'
                    //         },
                    //         {
                    //             optionsLabel: 'Invert',
                    //             optionValue: 'invert',
                    //             optionType: 'text'
                    //         },
                    //         {
                    //             optionsLabel: 'Layer',
                    //             optionValue: 'layer',
                    //             optionType: 'text'
                    //         },
                    //         {
                    //             optionsLabel: 'Lighten',
                    //             optionValue: 'lighten',
                    //             optionType: 'text'
                    //         },
                    //         {
                    //             optionsLabel: 'Multiply',
                    //             optionValue: 'multiply',
                    //             optionType: 'text'
                    //         },
                    //         {
                    //             optionsLabel: 'Overlay',
                    //             optionValue: 'overlay',
                    //             optionType: 'text'
                    //         },
                    //         {
                    //             optionsLabel: 'Screen',
                    //             optionValue: 'screen',
                    //             optionType: 'text'
                    //         },
                    //         {
                    //             optionsLabel: 'Subtract',
                    //             optionValue: 'subtract',
                    //             optionType: 'text'
                    //         }
                    //     ],
                    //     storePath: ''
                    // },
                ];
                break;
            }
            case 'Border': {
                this.uiConfigInit = [
                    borderColor,
                    borderThickness,
                ];
                break;
            }
            case 'Background': {
                this.uiConfigInit = [
                    color,
                    alpha,
                ];
                break;
            }
            case 'Blur': {
                this.uiConfigInit = [
                    {
                        type: "input",
                        value: '',
                        label: this.translocoService.translate('general.blurX', {}, this.translocoService.getActiveLang()),
                        inputType: "number_rich",
                        min: 0,
                        step: 1,
                        name: "blurX",
                        storePath: ''
                    },
                    {
                        type: "input",
                        value: '',
                        label: this.translocoService.translate('general.blurY', {}, this.translocoService.getActiveLang()),
                        inputType: "number_rich",
                        min: 0,
                        step: 1,
                        name: "blurY",
                        storePath: ''
                    },
                    resolution,
                ];
                break;
            }
            case 'Shadow': {
                this.uiConfigInit = [
                    color,
                    alpha,
                    blur,
                    {
                        type: "input",
                        value: '',
                        label: this.translocoService.translate('general.angle', {}, this.translocoService.getActiveLang()),
                        inputType: "number_rich",
                        min: 0,
                        step: 1,
                        name: "angle",
                        storePath: ''
                    },
                    {
                        type: "input",
                        value: '',
                        label: this.translocoService.translate('general.distance', {}, this.translocoService.getActiveLang()),
                        inputType: "number_rich",
                        min: 0,
                        step: 1,
                        name: "distance",
                        storePath: ''
                    },
                    {
                        type: "input",
                        value: '',
                        label: this.translocoService.translate('general.resolution', {}, this.translocoService.getActiveLang()),
                        inputType: "number_rich",
                        min: 0,
                        step: 1,
                        name: "resolution",
                        storePath: ''
                    },
                    {
                        type: "input",
                        value: '',
                        label: this.translocoService.translate('general.blurX', {}, this.translocoService.getActiveLang()),
                        inputType: "number_rich",
                        min: 0,
                        step: 1,
                        name: "blurX",
                        storePath: ''
                    },
                    {
                        type: "input",
                        value: '',
                        label: this.translocoService.translate('general.blurY', {}, this.translocoService.getActiveLang()),
                        inputType: "number_rich",
                        min: 0,
                        step: 1,
                        name: "blurY",
                        storePath: ''
                    },
                    {
                        type: "input",
                        value: '',
                        label: this.translocoService.translate('general.strength', {}, this.translocoService.getActiveLang()),
                        inputType: "number_rich",
                        min: 0,
                        step: 1,
                        name: "strength",
                        storePath: ''
                    },
                ];
                break;
            }
            case 'Glow': {
                this.uiConfigInit = [
                    color,
                    alpha,
                    blur,
                    resolution,
                ];
                break;
            }
            case 'Gradient': {
                this.uiConfigInit = [
                    {
                        type: "input",
                        value: '',
                        label: this.translocoService.translate('general.color', {}, this.translocoService.getActiveLang()),
                        inputType: "color",
                        name: "color0",
                        storePath: ''
                    },
                    {
                        type: "input",
                        value: '',
                        label: this.translocoService.translate('general.alpha', {}, this.translocoService.getActiveLang()),
                        inputType: "number_rich",
                        min: 0,
                        max: 1,
                        step: 0.1,
                        name: "alpha0",
                        storePath: ''
                    },
                    {
                        type: "input",
                        value: '',
                        label: this.translocoService.translate('general.color', {}, this.translocoService.getActiveLang()),
                        inputType: "color",
                        name: "color1",
                        storePath: ''
                    },
                    {
                        type: "input",
                        value: '',
                        label: this.translocoService.translate('general.alpha', {}, this.translocoService.getActiveLang()),
                        inputType: "number_rich",
                        min: 0,
                        max: 1,
                        step: 0.1,
                        name: "alpha1",
                        storePath: ''
                    },
                    {
                        type: "input",
                        value: '',
                        label: this.translocoService.translate('general.color', {}, this.translocoService.getActiveLang()),
                        inputType: "color",
                        name: "color2",
                        storePath: ''
                    },
                    {
                        type: "input",
                        value: '',
                        label: this.translocoService.translate('general.alpha', {}, this.translocoService.getActiveLang()),
                        inputType: "number_rich",
                        min: 0,
                        max: 1,
                        step: 0.1,
                        name: "alpha2",
                        storePath: ''
                    },
                    {
                        type: "input",
                        value: '',
                        label: this.translocoService.translate('general.color', {}, this.translocoService.getActiveLang()),
                        inputType: "color",
                        name: "color3",
                        storePath: ''
                    },
                    {
                        type: "input",
                        value: '',
                        label: this.translocoService.translate('general.alpha', {}, this.translocoService.getActiveLang()),
                        inputType: "number_rich",
                        min: 0,
                        max: 1,
                        step: 0.1,
                        name: "alpha3",
                        storePath: ''
                    },
                ];
                break;
            }
        }
    }

    override submit(formValues) {
        // Effects use split forms (color form + number form).
        // We need to:
        // 1. Filter out fields not in formValues (prevents cross-contamination)
        // 2. Remove empty color/date values (prevents crashes and unwanted resets)

        // Clean formValues - remove empty color and date fields
        const cleanedFormValues = {};
        Object.keys(formValues).forEach(key => {
            const value = formValues[key];
            const field = this.uiConfigInit.find(f => f && f.name === key);

            if (field) {
                // Skip empty color fields (would reset to black/0)
                if (field.inputType === 'color' && (!value || value === '')) {
                    return; // Don't include this field
                }
                // Skip empty date fields (would crash or reset)
                if (field.inputType === 'date' && (!value || value === '')) {
                    return; // Don't include this field
                }
            }

            // Include all other fields
            cleanedFormValues[key] = value;
        });

        // Temporarily filter uiConfigInit to only include fields present in cleanedFormValues
        const originalUiConfig = this.uiConfigInit;
        this.uiConfigInit = this.uiConfigInit.filter(config =>
            config && Object.prototype.hasOwnProperty.call(cleanedFormValues, config.name)
        );

        // Call parent submit with cleaned values and filtered config
        const result = super.submit(cleanedFormValues);

        // Restore original uiConfigInit
        this.uiConfigInit = originalUiConfig;

        return result;
    }

    applyPreset(preset: any) {
        // Check if scene and sceneItem are available
        if (!this.sceneId || !this.sceneItem || !this.sceneItem.$) {
            console.warn('Cannot apply preset: sceneId or sceneItem not available');
            return;
        }

        // Minimize advanced settings when preset is applied
        this.showAdvanced = false;

        // Build values array for store update
        const values = [];
        Object.keys(preset.values).forEach(key => {
            const field = this.uiConfigInit.find(f => f && f.name === key);
            if (field) {
                let storeValue = preset.values[key];

                // Convert color from hex to decimal for storage
                if ((key === 'color' || key.includes('Color') || key.startsWith('color')) && field.inputType === 'color') {
                    storeValue = Lib.HexToDecimal(preset.values[key]);
                }

                // Update field value for UI
                field.value = preset.values[key];
                this.setFormValue(key, preset.values[key], field.inputType);

                // Prepare store update
                const storePath = `Data[0].${this.effect}[0].$`;
                values.push({
                    key: key,
                    value: storeValue,
                    storePath: storePath
                });
            }
        });

        // Only dispatch if we have values to update
        if (values.length > 0) {
            this.dispatchSceneItemChanges({
                sceneId: this.sceneId,
                sceneItemId: this.sceneItem.$.id,
                values: values
            });

            this.cd.detectChanges();
        }
    }

    toggleAdvanced() {
        this.showAdvanced = !this.showAdvanced;
    }

    getColorFields() {
        return this.uiConfigInit.filter(f => f && (f.inputType === 'color' || f.type === 'select'));
    }

    getNumberFields() {
        return this.uiConfigInit.filter(f => f && f.inputType === 'number_rich');
    }

    resetEffect() {
        const defaultValues = this.getDefaultEffectValues();
        if (defaultValues) {
            this.applyPreset({
                name: 'reset',
                values: defaultValues
            });
        }
    }

    getDefaultEffectValues() {
        switch (this.effect) {
            case 'Shadow':
                return { color: '#000000', alpha: 0.2, blur: 4, angle: 45, distance: 3, resolution: 1, blurX: 2, blurY: 2, strength: 1 };
            case 'Blur':
                return { blurX: 2, blurY: 2, resolution: 1 };
            case 'Glow':
                return { color: '#ffffff', alpha: 0.5, blur: 4, resolution: 1 };
            case 'Border':
                return { borderColor: '#000000', borderThickness: 1 };
            case 'Background':
                return { color: '#000000', alpha: 0 };
            case 'Gradient':
                return {
                    color0: '#667eea', alpha0: 1,
                    color1: '#764ba2', alpha1: 1,
                    color2: '#667eea', alpha2: 0,
                    color3: '#000000', alpha3: 0
                };
            case 'Appearance':
                return { alpha: 1 }; // blendMode removed - not supported yet
            default:
                return null;
        }
    }

    devExportConfig() {
        if (!this.sceneItem) {
            console.warn('Cannot export config: sceneItem not available');
            return;
        }

        const value: IEffectTypeAny = _.get(this.sceneItem, `Data[0].${this.effect}[0]`);
        if (!value || !value.$) {
            console.warn(`Effect ${this.effect} not found in sceneItem`);
            return;
        }

        const animation: IEffectTypeAny = _.cloneDeep(value);
        animation.$.name = 'name here';
        animation.$.image = 'image.gif';
        console.log(JSON.stringify(animation));
    }
}


